/**
 * render-our-view-out-paper — 우리 뷰 중 **지면 밖(out-paper)** 세 겹(순수 HTML 빌더):
 *   여백(왼쪽 거터) · 요약 카드(오른쪽) · 하단바.  [고객 뷰] = 루트 클래스 하나로 셋을 숨김.
 * ★ 우리 뷰의 구성요소 2가지 — 가르는 축은 지면 안/밖:
 *   in-paper  우리 줄(소스·원가·마진) — paper-2-fill 이 지면 격자 안에 심는다.
 *   out-paper 세 겹 — 이 파일. 지면 밖에 별도 층으로 그려진다.
 *   어느 쪽도 고객에게 안 실린다(실리느냐가 우리 뷰/고객 뷰의 경계).
 * DOM/프레임워크 모름 — 문자열만 만든다(테스트 대상). View 가 배선/이벤트를 얹는다.
 * (설계: docs/QUOTE_ONEPAGE_DESIGN.md §3.2~3.6 · 개명 이력: render-edit → internal(퇴역어)
 *  → our-view(과잉 주장) → our-view-out-paper — 2026-08-31, 어휘 1호 구성요소 판정)
 *
 * 데이터 주의: 원가·마진·소스등급(A~D)·attested 는 아직 편집 API 미연동(소싱 조인·307).
 * 그 슬롯은 자리를 잡되 "—/미연동"으로 정직하게 비워둔다 — 붙는 순간 값만 채우면 된다.
 */
import type { QuoteItem, QuoteCtx } from './paper-1-specs';
import type { OutPaperItem, Totals } from './paper-2-fill';

export interface OurViewState {
  selectedIdx: number | null;  // 여백 카드에서 강조되는 행(null = 없음)
  seen: number[];              // 사람이 여백 카드를 클릭해 검토한 행(데모). 실제 영속은 307
  isCustomerView: boolean;
  isServer: boolean;           // 서버 견적인가 — 정식/약식 토글은 서버에만
  isBrief: boolean;            // 약식(서버 요약) on/off
}

/** 원화 숫자 문구 — 없으면 '—' (미연동 슬롯의 정직한 빈칸). */
const wonText = (amount: number | null | undefined) => (amount == null ? '—' : amount.toLocaleString('ko-KR'));

/* ─────────────────────────  여백 (왼쪽 거터)  ───────────────────────── */
/** 지면과 같은 grid-template-rows 로 깔아 행에 픽셀 정렬. 행 높이가 낮아 카드는 한 줄. */
export function renderGutter(
  rowHeightsPx: number[],
  outPaperItems: OutPaperItem[],
  state: OurViewState,
): string {
  const gridRowsCss = rowHeightsPx.map((heightPx) => `${heightPx}px`).join(' ');
  const cards = outPaperItems.map((outPaperItem) => {
    const isSeen = state.seen.includes(outPaperItem.itemIndex);
    const isSelected = state.selectedIdx === outPaperItem.itemIndex;
    const dot = isSeen ? '<span class="qg-dot qg-ok">●</span>' : '<span class="qg-dot qg-no">○</span>';
    return `<div class="qg-card${isSelected ? ' qg-sel' : ''}" style="grid-row:${outPaperItem.gridRowStart}/span ${outPaperItem.span}"
      data-action="qe-row" data-idx="${outPaperItem.itemIndex}" title="검토됨으로 표시">
      ${dot}<span class="qg-lbl">${isSeen ? '검토' : '미검토'}</span><span class="qg-grade">등급 —</span>
    </div>`;
  }).join('');
  return `<div class="qe-gutter" style="grid-template-rows:${gridRowsCss}">${cards}</div>`;
}

/* ─────────────────────────  요약 카드 (오른쪽)  ───────────────────────── */
/**
 * 견적 요약 — 적용 환율 · 공급가액 · 부가세 · 합계. 지면의 SUMMARY/푸터 숫자와 같은 값(totals 는 숫자 사본).
 * 행 컨텍스트(레일)는 퇴역: 행의 세계는 지면 안 우리 줄·소스 팝오버·매입 후보 패널이 맡는다.
 * 안 본 행 수는 하단바 게이트, 행별 RFQ 요청은 소스 팝오버의 버튼.
 */
export function renderSummaryCard(ctx: QuoteCtx, totals: Totals): string {
  const fxLine = ctx.krwPerUsd > 0
    ? `<div class="qsm-fx"><span class="qsm-fx-k">적용 환율 · 오늘</span><span class="qsm-fx-v">$1 = ₩${wonText(Math.round(ctx.krwPerUsd))}</span></div>`
    : '';
  return `<div class="qe-summary">
    <div class="qsm-h">견적 요약</div>
    ${fxLine}
    <div class="qsm-row"><span class="qsm-k">공급가액</span><span class="qsm-v">₩${wonText(totals.net)}</span></div>
    <div class="qsm-row"><span class="qsm-k">부가세 (10%)</span><span class="qsm-v">₩${wonText(totals.vat)}</span></div>
    <div class="qsm-row qsm-total"><span class="qsm-k">합계</span><span class="qsm-v">₩${wonText(totals.total)}</span></div>
  </div>`;
}

/* ─────────────────────────  하단 바  ───────────────────────── */
export function renderBottomBar(
  items: QuoteItem[],
  totals: Totals,
  state: OurViewState,
): string {
  const unseen = items.filter((_, itemIndex) => !state.seen.includes(itemIndex)).length;
  const isCustomerViewOn = state.isCustomerView;
  return `<div class="qe-bottombar">
    <div class="qb-nums">
      <span class="qb-k">판매</span><span class="qb-v">₩${wonText(totals.net)}</span>
      <span class="qb-k">VAT</span><span class="qb-v">₩${wonText(totals.vat)}</span>
      <span class="qb-k qb-strong">합계</span><span class="qb-v qb-strong">₩${wonText(totals.total)}</span>
      <span class="qb-sep">·</span>
      <span class="qb-k">원가·마진</span><span class="qb-v qb-dim">미연동</span>
      <span class="qb-sep">·</span>
      <span class="qb-gate${unseen ? ' qb-warn' : ''}">안 본 행 ${unseen}</span>
    </div>
    <div class="qb-actions">
      <button class="qb-btn" type="button" data-action="qe-add-open">＋ 항목 추가</button>
      <button class="qb-btn" type="button" data-action="qe-export-xlsx">⤓ 엑셀 출력</button>
      <button class="qb-btn" type="button" data-action="qe-save">저장</button>
${state.isServer ? `<button class="qb-btn qb-brief${state.isBrief ? ' on' : ''}" type="button" data-action="qe-brief">${state.isBrief ? '약식' : '정식'}</button>` : ''}
      <button class="qb-btn qb-view${isCustomerViewOn ? ' on' : ''}" type="button" data-action="qe-view">${isCustomerViewOn ? '우리 뷰로' : '고객 뷰'}</button>
      <button class="qb-btn qb-send" type="button" data-action="qe-send">발송${unseen ? ` — 게이트 ${unseen}` : ''}</button>
    </div>
  </div>`;
}

/* ─────────────────────────  CSS  ───────────────────────── */
export const OUR_VIEW_OUT_PAPER_CSS = `
.qe-shell{position:relative;display:flex;align-items:flex-start;justify-content:center;gap:14px;padding:24px 20px 84px}   /* relative — 매입 후보 슬롯(절대위치)의 기준 */
.qe-paper-wrap .paper{box-shadow:0 3px 22px rgba(10,25,35,.2)}

/* 여백 (왼쪽 거터) — 지면과 같은 행 격자로 픽셀 정렬 */
.qe-gutter{display:grid;width:132px;flex:0 0 132px;align-self:flex-start;font:11px/1 system-ui,sans-serif}
.qg-card{border-left:2px solid #d7dde2;padding:3px 7px 0;display:flex;align-items:flex-start;gap:5px;cursor:pointer;color:#5a6771;overflow:hidden;white-space:nowrap}
.qg-card:hover{background:#f0f4f7;border-left-color:#9aa7b1}
.qg-card.qg-sel{background:#eaf1fb;border-left:2px solid #2f6fed;color:#1f3a63;font-weight:600}
.qg-dot{font-size:9px}.qg-ok{color:#1a9d63}.qg-no{color:#e0851b}
.qg-lbl{min-width:30px}
.qg-grade{color:#aab4bd;font-size:10px;margin-left:auto}

/* 요약 카드 (오른쪽) */
.qe-summary{width:270px;flex:0 0 270px;align-self:flex-start;background:#fff;border:1px solid #e2e8ed;border-radius:10px;padding:14px 16px;font:13px/1.5 system-ui,sans-serif;color:#33414c;box-shadow:0 2px 14px rgba(10,25,35,.06)}
.qsm-h{font-weight:700;font-size:14px;color:#16212b;margin-bottom:9px}
.qsm-fx{display:flex;justify-content:space-between;align-items:center;padding:7px 10px;background:#f4f7fb;border:1px solid #e2eaf3;border-radius:8px;margin-bottom:9px}
.qsm-fx-k{font-size:11px;color:#7c8894}.qsm-fx-v{font:700 13px system-ui;color:#1f3a63;font-variant-numeric:tabular-nums}
.qsm-row{display:flex;justify-content:space-between;padding:4px 0;font-size:12.5px}
.qsm-k{color:#8996a1}.qsm-v{font-weight:600;font-variant-numeric:tabular-nums}
.qsm-total{border-top:1px solid #eef1f4;margin-top:4px;padding-top:7px}.qsm-total .qsm-v{font-weight:800;font-size:14px;color:#16212b}

/* 하단 바 */
.qe-bottombar{position:fixed;left:0;right:0;bottom:0;height:60px;background:#16212b;color:#e7edf1;display:flex;align-items:center;justify-content:space-between;padding:0 22px;font:13px/1 system-ui,sans-serif;box-shadow:0 -2px 16px rgba(10,25,35,.25);z-index:20}
.qb-nums{display:flex;align-items:center;gap:8px}
.qb-k{color:#93a1ad}.qb-v{font-weight:600}.qb-strong{color:#fff}.qb-dim{color:#7d8b97}
.qb-sep{color:#3f4d59;margin:0 3px}
.qb-gate{padding:3px 9px;border-radius:20px;background:#24313d;color:#a9b6c1}
.qb-gate.qb-warn{background:#5a3417;color:#f6c188}
.qb-actions{display:flex;gap:9px}
.qb-btn{padding:8px 16px;border-radius:7px;border:1px solid #38454f;background:#22303b;color:#e7edf1;font:600 13px system-ui;cursor:pointer}
.qb-btn:hover{background:#2b3a46}
.qb-view.on{background:#e7edf1;color:#16212b}
.qb-send{background:#2f6fed;border-color:#2f6fed;color:#fff}.qb-send:hover{background:#255fd6}

/* [고객 뷰] — 편집 세 겹을 숨기고 지면만 */
.customer-view .qe-gutter,.customer-view .qe-summary,.customer-view .qe-bottombar{display:none}
.customer-view .qe-shell{padding-bottom:24px}
/* [편집으로] 복귀 — 하단바가 숨어도 항상 보이는 플로팅 버튼(고객 뷰일 때만) */
.qe-view-return{display:none;position:fixed;right:20px;bottom:20px;z-index:30;padding:10px 18px;border-radius:24px;border:0;background:#16212b;color:#fff;font:600 13px system-ui;cursor:pointer;box-shadow:0 4px 16px rgba(10,25,35,.3)}
.qe-view-return:hover{background:#22303b}
.customer-view .qe-view-return{display:block}
`;
